import { Component, OnInit, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ServicioSap } from 'src/app/domain/model/servicio-sap/servicio-sap';
import {
  SERVICIO_SAP_CODIGO_MAX_LENGTH,
  SERVICIO_SAP_DESCRIPCION_MAX_LENGTH
} from 'src/app/common/constants-ejb';
import {
  onlyNumbersKeypress,
  sanitizeNumbersOnly,
  sanitizeTextPreserveSpaces
} from 'src/app/common/input-utils';

/**
 * Modal (dumb component) para crear o modificar un servicio SAP (HU1287272).
 *
 * <p>Legacy: RegistroServiciosSapViewImpl. Subtítulo "Ingresar nuevo servicio" / "Modificar servicio";
 * "Código servicio" obligatorio, solo dígitos (máx. 8) y deshabilitado al modificar; "Descripción del
 * servicio" obligatoria (máx. 50). La validación y el guardado viven en el facade: el componente solo
 * recoge los valores y emite {@link aceptar}.</p>
 *
 * <p>El container lo instancia solo mientras está visible, así que cada apertura parte de cero.</p>
 */
@Component({
  selector: 'app-servicio-sap-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule
  ],
  template: `
    <p-dialog
      header="Servicio SAP"
      [visible]="true"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [closable]="true"
      [style]="{ width: '92vw', maxWidth: '34rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '640px': '95vw' }"
      (onHide)="cancelar.emit()"
    >
      <div class="flex items-center justify-between gap-2 mb-3">
        <h4
          class="m-0 text-base font-semibold text-gray-700 dark:text-gray-200"
        >
          {{ subtitulo() }}
        </h4>
        <p-button
          label="Salir"
          icon="pi pi-sign-out"
          severity="secondary"
          [text]="true"
          styleClass="min-h-[44px]"
          (onClick)="cancelar.emit()"
        ></p-button>
      </div>

      @if (mensaje()) {
        <div
          class="p-3 mb-3 rounded border"
          [ngClass]="{
            'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-700':
              mensajeTipo() === 'warning',
            'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700':
              mensajeTipo() === 'error'
          }"
          role="alert"
        >
          {{ mensaje() }}
        </div>
      }

      <div class="grid grid-cols-1 gap-y-3">
        <div>
          <label
            for="sap-codigo"
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Código servicio *</label
          >
          <input
            id="sap-codigo"
            pInputText
            class="w-full"
            aria-required="true"
            [attr.aria-invalid]="mensaje() ? 'true' : null"
            inputmode="numeric"
            autocomplete="off"
            [maxlength]="codigoMaxLength"
            [disabled]="modo() === 'editar'"
            [(ngModel)]="codigo"
            (keypress)="soloNumeros($event)"
            (input)="codigo = sanitizeNumbersOnly(codigo)"
          />
        </div>
        <div>
          <label
            for="sap-descripcion"
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Descripción del servicio *</label
          >
          <input
            id="sap-descripcion"
            pInputText
            class="w-full"
            aria-required="true"
            [attr.aria-invalid]="mensaje() ? 'true' : null"
            autocomplete="off"
            [maxlength]="descripcionMaxLength"
            [(ngModel)]="descripcion"
            (input)="onDescripcionInput($event)"
            (keyup.enter)="aceptarClick()"
          />
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cancelar"
            icon="pi pi-times"
            severity="secondary"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="cancelar.emit()"
          ></p-button>
          <p-button
            label="Aceptar"
            icon="pi pi-check"
            styleClass="min-h-[44px]"
            [loading]="guardando()"
            (onClick)="aceptarClick()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class ServicioSapDialogComponent implements OnInit {
  readonly modo = input.required<'crear' | 'editar'>();
  readonly servicio = input<ServicioSap | null>(null);
  readonly mensaje = input('');
  readonly mensajeTipo = input<'warning' | 'error'>('warning');
  readonly guardando = input(false);

  readonly aceptar = output<{ cdservicio: string; dsservicio: string }>();
  readonly cancelar = output<void>();

  protected readonly codigoMaxLength = SERVICIO_SAP_CODIGO_MAX_LENGTH;
  protected readonly descripcionMaxLength = SERVICIO_SAP_DESCRIPCION_MAX_LENGTH;
  protected readonly sanitizeNumbersOnly = sanitizeNumbersOnly;
  protected readonly soloNumeros = onlyNumbersKeypress;

  codigo = '';
  descripcion = '';

  ngOnInit(): void {
    const servicio = this.servicio();
    this.codigo = servicio?.cdservicio ?? '';
    // Se muestra en mayúsculas lo que se va a guardar; la tabla conserva el valor tal como está en BD.
    this.descripcion = (servicio?.dsservicio ?? '').toUpperCase();
  }

  /**
   * Convierte a mayúsculas mientras se escribe (también al pegar) y limpia contenido HTML/XSS, sin
   * mover el cursor cuando se edita en medio del texto.
   */
  protected onDescripcionInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cursor = input.selectionStart;
    const valor = sanitizeTextPreserveSpaces(input.value).toUpperCase();
    if (valor !== input.value) {
      input.value = valor;
      if (cursor !== null) input.setSelectionRange(cursor, cursor);
    }
    this.descripcion = valor;
  }

  subtitulo(): string {
    // Legacy: RegistroServiciosSapViewImpl — subtítulo según el modo del modal.
    return this.modo() === 'crear'
      ? 'Ingresar nuevo servicio'
      : 'Modificar servicio';
  }

  aceptarClick(): void {
    // Evita el doble envío (Enter o clic repetido) mientras se guarda.
    if (this.guardando()) return;
    this.aceptar.emit({
      cdservicio: this.codigo,
      dsservicio: this.descripcion
    });
  }
}
